import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AppNav from '../AppNav/AppNav';

const FW = (value) =>
  value.replace(/[０-９]/g, (character) =>
    String.fromCharCode(character.charCodeAt(0) - 0xfee0),
  );

const toInt = (value) => parseInt(FW(String(value)), 10);

function normalizeInput(text) {
  return String(text || '').replace(/\r\n?/g, '\n').trim();
}

function normalizeClaimBody(text, lang) {
  const separator = lang === 'ja' ? '' : ' ';
  return String(text || '')
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t\u00a0]*\n+[ \t\u00a0]*/g, separator)
    .replace(/[ \t\u00a0]{2,}/g, ' ')
    .trim();
}

function parseJa(text) {
  const claims = [];
  const pattern = /【請求項([０-９\d]+)】([\s\S]*?)(?=【請求項[０-９\d]+】|$)/g;
  let match;
  while ((match = pattern.exec(text)) !== null) {
    claims.push({
      num: toInt(match[1]),
      text: normalizeClaimBody(match[2], 'ja'),
    });
  }
  if (claims.length) {
    return claims;
  }
  const lines = text.split('\n');
  let current = null;
  lines.forEach((line) => {
    const normalizedLine = line.trim();
    const localMatch =
      normalizedLine.match(/^(?:請求項|クレーム)\s*([０-９\d]+)[．.。\s](.*)/) ||
      normalizedLine.match(/^([０-９\d]+)[．.)）]\s*(.*)/);
    if (localMatch) {
      if (current) {
        claims.push(current);
      }
      current = {
        num: toInt(localMatch[1]),
        text: normalizeClaimBody(localMatch[2], 'ja'),
      };
    } else if (current && normalizedLine) {
      current.text = normalizeClaimBody(`${current.text}${normalizedLine}`, 'ja');
    }
  });
  if (current) {
    claims.push(current);
  }
  return claims;
}

function parseEn(text) {
  const claims = [];
  const lines = text.split('\n');
  let current = null;
  lines.forEach((line) => {
    const normalizedLine = line.trim();
    const match = normalizedLine.match(/^(\d+)[.)]\s+(.*)/);
    if (match) {
      if (current) {
        claims.push(current);
      }
      current = {
        num: parseInt(match[1], 10),
        text: normalizeClaimBody(match[2], 'en'),
      };
    } else if (current && normalizedLine) {
      current.text = normalizeClaimBody(`${current.text} ${normalizedLine}`, 'en');
    }
  });
  if (current) {
    claims.push(current);
  }
  return claims;
}

function depsJa(text, own) {
  const numbers = [];
  const normalized = FW(String(text || '')).replace(/第\s*(\d+)/g, '$1');
  const pattern =
    /請求項\s*([0-9\s、,，及びおよび又はまたは若しくはもしくはから乃至ないし〜～\-－–—]+)/g;

  let chunkMatch;
  while ((chunkMatch = pattern.exec(normalized)) !== null) {
    const chunk = chunkMatch[1];

    const rangePattern = /(\d+)\s*(?:から|乃至|ないし|〜|～|\-|－|–|—)\s*(\d+)/g;
    let rangeMatch;
    while ((rangeMatch = rangePattern.exec(chunk)) !== null) {
      const start = parseInt(rangeMatch[1], 10);
      const end = parseInt(rangeMatch[2], 10);
      if (Number.isNaN(start) || Number.isNaN(end)) {
        continue;
      }
      const lower = Math.min(start, end);
      const upper = Math.max(start, end);
      for (let number = lower; number <= upper; number += 1) {
        if (number !== own) {
          numbers.push(number);
        }
      }
    }

    const numberPattern = /(\d+)/g;
    let numberMatch;
    while ((numberMatch = numberPattern.exec(chunk)) !== null) {
      const number = parseInt(numberMatch[1], 10);
      if (!Number.isNaN(number) && number !== own) {
        numbers.push(number);
      }
    }
  }

  if (!numbers.length) {
    const fallbackPattern = /請求項([０-９\d]+)/g;
    let fallbackMatch;
    while ((fallbackMatch = fallbackPattern.exec(text)) !== null) {
      const number = toInt(fallbackMatch[1]);
      if (number !== own) {
        numbers.push(number);
      }
    }
  }

  return [...new Set(numbers)];
}

function depsEn(text, own) {
  const numbers = [];
  const pattern =
    /\bclaims?\s+((?:(?:\d+\s*(?:-|–|—|\bto\b|\bthrough\b)\s*\d+)|\d+)(?:[\s,]+(?:and|or)?[\s,]*(?:(?:\d+\s*(?:-|–|—|\bto\b|\bthrough\b)\s*\d+)|\d+))*)/gi;
  let match;
  while ((match = pattern.exec(String(text || ''))) !== null) {
    const chunk = match[1].replace(/[–—]/g, '-');
    const rangePattern = /(\d+)\s*(?:-|\bto\b|\bthrough\b)\s*(\d+)/gi;
    let rangeMatch;

    while ((rangeMatch = rangePattern.exec(chunk)) !== null) {
      const start = parseInt(rangeMatch[1], 10);
      const end = parseInt(rangeMatch[2], 10);
      if (Number.isNaN(start) || Number.isNaN(end)) {
        continue;
      }
      const lower = Math.min(start, end);
      const upper = Math.max(start, end);
      for (let number = lower; number <= upper; number += 1) {
        if (number !== own) {
          numbers.push(number);
        }
      }
    }

    const remainder = chunk.replace(rangePattern, ' ');
    const numberPattern = /(\d+)/g;
    let numberMatch;
    while ((numberMatch = numberPattern.exec(remainder)) !== null) {
      const number = parseInt(numberMatch[1], 10);
      if (!Number.isNaN(number) && number !== own) {
        numbers.push(number);
      }
    }
  }
  return [...new Set(numbers.filter((number) => number !== own))];
}

function buildChain(num, parentMap, visited = new Set()) {
  if (visited.has(num)) {
    return [num];
  }
  visited.add(num);
  const parents = parentMap[num] || [];
  if (!parents.length) {
    return [num];
  }
  return [num, ...buildChain(parents[0], parentMap, visited)];
}

export default function DependencyCheckerPage() {
  const [text, setText] = useState('');
  const [lang, setLang] = useState('auto');
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);

  function runCheck() {
    const normalizedInput = normalizeInput(text);
    if (!normalizedInput) {
      return;
    }
    const effectiveLang =
      lang === 'auto' ? (/[ぁ-ん]/.test(normalizedInput) ? 'ja' : 'en') : lang;
    const claims =
      effectiveLang === 'ja'
        ? parseJa(normalizedInput)
        : parseEn(normalizedInput);
    if (!claims.length) {
      window.alert('Claims could not be recognized.');
      return;
    }

    const depsOf = {};
    claims.forEach((claim) => {
      depsOf[claim.num] =
        effectiveLang === 'ja'
          ? depsJa(claim.text, claim.num)
          : depsEn(claim.text, claim.num);
    });

    const isMultiDep = (num) => (depsOf[num] || []).length >= 2;
    const isMultiMulti = (num) =>
      isMultiDep(num) && (depsOf[num] || []).some((parent) => isMultiDep(parent));

    let mmCount = 0;
    let multiCount = 0;
    const nextRows = claims.map((claim) => {
      const deps = depsOf[claim.num] || [];
      const multi = isMultiDep(claim.num);
      const mm = isMultiMulti(claim.num);
      if (mm) {
        mmCount += 1;
      }
      if (multi) {
        multiCount += 1;
      }
      return {
        num: claim.num,
        deps,
        multi,
        mm,
        lang: effectiveLang,
        chain: buildChain(claim.num, depsOf),
        problematicParents: deps.filter((parent) => isMultiDep(parent)),
      };
    });

    setRows(nextRows);
    setSummary({ mmCount, multiCount, lang: effectiveLang });
  }

  return (
    <main className="patent-tool">
      <section className="patent-tool__shell">
        <AppNav currentPath="/tools" />
        <header className="patent-tool__hero">
          <p className="patent-tool__eyebrow">Claim Check</p>
          <h1>Claim dependency checker</h1>
          <p className="patent-tool__note">
            Detect multi-multi dependencies in Japanese or English claims without leaving the browser.
          </p>
          <div className="patent-tool__actions">
            <button className="patent-entry__submit" type="button" onClick={runCheck}>
              Run check
            </button>
            <Link className="patent-entry__ghost" to="/">
              Back to overview
            </Link>
          </div>
        </header>

        <section className="patent-tool__card">
          <div className="patent-tool__card-head">
            <span className="patent-tool__step">1. Claims input</span>
            <select className="patent-tool__select" value={lang} onChange={(event) => setLang(event.target.value)}>
              <option value="auto">Auto</option>
              <option value="ja">Japanese</option>
              <option value="en">English</option>
            </select>
          </div>
          <textarea
            className="patent-entry__textarea patent-tool__textarea"
            rows="14"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="【請求項１】...&#10;【請求項２】請求項１に記載の...&#10;【請求項３】請求項１または２に記載の..."
          />
        </section>

        {rows ? (
          <section className="patent-tool__card">
            <div className="patent-tool__card-head">
              <span className="patent-tool__step">2. Results</span>
              <span className={summary.mmCount ? 'patent-tool__summary patent-tool__summary--danger' : 'patent-tool__summary patent-tool__summary--ok'}>
                {summary.mmCount
                  ? `Multi-multi ${summary.mmCount}, multi-dependent ${summary.multiCount}`
                  : summary.multiCount
                    ? `No multi-multi, multi-dependent ${summary.multiCount}`
                    : 'No issue found'}
              </span>
            </div>
            <div className="patent-tool__table-wrap">
              <table className="patent-tool__table">
                <thead>
                  <tr>
                    <th>Claim</th>
                    <th>Type</th>
                    <th>Referenced claims</th>
                    <th>Dependency chain</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.num} className={row.mm ? 'patent-tool__row--warn' : ''}>
                      <td className="patent-tool__claim-num">
                        {row.lang === 'ja' ? `請求項${row.num}` : `Claim ${row.num}`}
                      </td>
                      <td>
                        {row.mm ? (
                          <span className="patent-tool__badge patent-tool__badge--danger">Multi-Multi</span>
                        ) : row.multi ? (
                          <span className="patent-tool__badge patent-tool__badge--info">Multi-dependent</span>
                        ) : row.deps.length === 1 ? (
                          <span className="patent-tool__badge patent-tool__badge--ok">Dependent</span>
                        ) : (
                          <span className="patent-tool__badge">Independent</span>
                        )}
                      </td>
                      <td>
                        {row.deps.length ? (
                          <>
                            {row.deps.map((dep) => (
                              <span
                                key={`${row.num}-${dep}`}
                                className={`patent-tool__pill${row.problematicParents.includes(dep) ? ' patent-tool__pill--warn' : ''}`}
                              >
                                {row.lang === 'ja' ? `請求項${dep}` : `Claim ${dep}`}
                              </span>
                            ))}
                            {row.problematicParents.length ? (
                              <div className="patent-tool__small">
                                {`Problematic parent: ${row.problematicParents
                                  .map((dep) =>
                                    row.lang === 'ja' ? `請求項${dep}` : `Claim ${dep}`,
                                  )
                                  .join(', ')}`}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="patent-tool__small">
                        {row.chain.length > 1 ? row.chain.join(' › ') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
      </section>
    </main>
  );
}
